<template>
  <div class="conversation-main">
      <div class="message-board">
      <el-empty v-if="!messages.length" description="发起第一条问题，系统会结合当前知识库返回结果" />
      <div v-else class="message-list">
        <div v-for="item in messages" :key="item.id + item.createTime" class="message-row" :class="[item.role]">
          <div class="message-role">{{ item.role === 'assistant' ? '助手' : '你' }}</div>
          <div class="message-item" :class="[item.role]">
            <div
              v-if="item.role === 'assistant'"
              class="message-content markdown-body"
              v-html="renderAssistantMessage(item.content)"
            ></div>
            <div v-else class="message-content user-text">{{ item.content }}</div>
            <div class="message-time">{{ formatMessageTime(item.updateTime || item.createTime) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="composer">
      <div class="composer-toolbar">
        <div class="composer-hint">输入 `@` 引用知识库，和对话内容一起发送。</div>
      </div>

      <div class="composer-input-wrap">
        <div v-if="mentionedKnowledgeBases.length" class="mention-chip-list">
          <span v-for="item in mentionedKnowledgeBases" :key="item.id" class="mention-chip">
            @{{ item.name }}
          </span>
        </div>

        <div v-if="showMention" class="mention-dropdown">
          <div v-if="filteredMentionKnowledgeBases.length" class="mention-list">
            <div
              v-for="(item, index) in filteredMentionKnowledgeBases"
              :key="item.id"
              class="mention-item"
              :class="{ active: index === mentionActiveIndex }"
              data-track-text="选择RAG提及知识库"
              aria-label="选择RAG提及知识库"
              @mousedown.prevent="selectMention(item)"
              @mouseenter="mentionActiveIndex = index"
            >
              <div class="mention-item-name">@{{ item.name }}</div>
              <div class="mention-item-meta">{{ item.documentCount || item.knowledgeCount || 0 }} 个文档</div>
            </div>
          </div>
          <div v-else class="mention-empty">没有匹配到知识库</div>
        </div>

        <el-input
          ref="messageInput"
          v-model="messageValue"
          type="textarea"
          :rows="4"
          resize="none"
          placeholder="输入问题，使用 @知识库名 引用知识库。"
          data-track-ignore
          @focus="$emit('require-auth')"
          @keydown.native="handleTextareaKeydown"
          @keyup.native="handleTextareaCursorChange"
          @click.native="handleTextareaCursorChange"
          @input="handleTextareaInput"
        />
      </div>

      <div class="composer-actions">
        <el-button class="composer-send-btn" data-track-text="发送RAG问题" aria-label="发送RAG问题" type="primary" :loading="sending" @click="$emit('send-message')">发送</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it/dist/markdown-it'

const markdownRender = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true
})

export default {
  name: 'RagConversationPanel',
  props: {
    knowledgeBases: {
      type: Array,
      default: () => []
    },
    messages: {
      type: Array,
      default: () => []
    },
    messageText: {
      type: String,
      default: ''
    },
    currentSessionId: {
      type: String,
      default: ''
    },
    selectedKnowledgeBaseIds: {
      type: Array,
      default: () => []
    },
    sending: {
      type: Boolean,
      default: false
    },
    sessions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    filteredMentionKnowledgeBases() {
      return this.getFilteredMentionKnowledgeBases(this.mentionQuery)
    },
    mentionedKnowledgeBases() {
      const text = this.messageText || ''
      const matches = []
      const seen = new Set()
      ;(this.knowledgeBases || []).forEach(item => {
        const name = String(item.name || '')
        if (!name) {
          return
        }
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const pattern = new RegExp(`(^|\\s)@${escapedName}(?=\\s|$)`)
        if (pattern.test(text) && !seen.has(item.id)) {
          seen.add(item.id)
          matches.push(item)
        }
      })
      return matches
    },
    knowledgeBaseValue: {
      get() {
        return this.selectedKnowledgeBaseIds
      },
      set(val) {
        this.$emit('change-knowledge-bases', val)
      }
    },
    messageValue: {
      get() {
        return this.messageText
      },
      set(val) {
        this.$emit('update-message', val)
      }
    }
  },
  data() {
    return {
      showMention: false,
      mentionQuery: '',
      mentionStartIndex: -1,
      mentionActiveIndex: 0
    }
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        this.scrollToBottom()
      }
    },
    mentionedKnowledgeBases: {
      immediate: true,
      handler(value) {
        this.$emit('change-knowledge-bases', value.map(item => item.id))
      }
    },
    knowledgeBases() {
      this.syncMentionSelection()
    }
  },
  methods: {
    getTextareaEl() {
      return this.$refs.messageInput && this.$refs.messageInput.$refs
        ? this.$refs.messageInput.$refs.textarea
        : null
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const board = this.$el ? this.$el.querySelector('.message-board') : null
        if (!board) {
          return
        }
        board.scrollTop = board.scrollHeight
      })
    },
    formatMessageTime(value) {
      if (!value) {
        return '-'
      }
      const text = String(value).trim()
      const matched = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/)
      if (matched) {
        return `${matched[1]} ${matched[2]}`
      }
      const normalized = text
        .replace('T', ' ')
        .replace(/\.\d+Z?$/, '')
        .replace(/Z$/, '')
      const date = new Date(normalized.replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) {
        return normalized
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    },
    renderAssistantMessage(content) {
      const normalized = this.normalizeCitationTags(content)
      return markdownRender.render(normalized)
    },
    normalizeCitationTags(content) {
      const text = String(content || '')
      return text
        .replace(/<kb\s+([^>]*?)\/?>/gi, (_, attributes) => {
          const documentName = this.extractTagAttribute(attributes, 'doc') || '知识库文档'
          return ` \`来源：${documentName}\` `
        })
        .replace(/<web\s+([^>]*?)\/?>/gi, (_, attributes) => {
          const title = this.extractTagAttribute(attributes, 'title') || '网页来源'
          const url = this.extractTagAttribute(attributes, 'url')
          return url ? ` [来源：${title}](${url}) ` : ` \`来源：${title}\` `
        })
    },
    extractTagAttribute(attributes, attributeName) {
      const pattern = new RegExp(`${attributeName}="([^"]*)"`, 'i')
      const matched = String(attributes || '').match(pattern)
      return matched && matched[1] ? matched[1] : ''
    },
    handleTextareaInput() {
      this.updateMentionState()
    },
    handleTextareaCursorChange(event) {
      if (event && this.isMentionNavigationKey(event.key)) {
        return
      }
      this.updateMentionState()
    },
    isMentionNavigationKey(key) {
      return ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(key)
    },
    getFilteredMentionKnowledgeBases(queryText) {
      const query = String(queryText || '').trim().toLowerCase()
      const list = this.knowledgeBases || []
      if (!query) {
        return list
      }
      return list.filter(item => String(item.name || '').toLowerCase().indexOf(query) > -1)
    },
    handleTextareaKeydown(event) {
      if (!this.showMention && event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault()
        this.$emit('send-message')
        return
      }
      if (!this.showMention) {
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!this.filteredMentionKnowledgeBases.length) {
          return
        }
        this.mentionActiveIndex = (this.mentionActiveIndex + 1) % this.filteredMentionKnowledgeBases.length
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!this.filteredMentionKnowledgeBases.length) {
          return
        }
        this.mentionActiveIndex = (this.mentionActiveIndex - 1 + this.filteredMentionKnowledgeBases.length) % this.filteredMentionKnowledgeBases.length
        return
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        if (!this.filteredMentionKnowledgeBases.length) {
          return
        }
        event.preventDefault()
        const item = this.filteredMentionKnowledgeBases[this.mentionActiveIndex]
        if (item) {
          this.selectMention(item)
        }
        return
      }
      if (event.key === 'Escape') {
        this.closeMention()
      }
    },
    updateMentionState() {
      const textarea = this.getTextareaEl()
      if (!textarea) {
        this.closeMention()
        return
      }
      const value = this.messageText || ''
      const caret = textarea.selectionStart || 0
      const beforeCaret = value.slice(0, caret)
      const atIndex = beforeCaret.lastIndexOf('@')
      if (atIndex < 0) {
        this.closeMention()
        return
      }
      const prefix = beforeCaret.slice(0, atIndex)
      if (prefix && !/\s$/.test(prefix)) {
        this.closeMention()
        return
      }
      const query = beforeCaret.slice(atIndex + 1)
      if (/[\s\n]/.test(query)) {
        this.closeMention()
        return
      }
      const filteredMentionKnowledgeBases = this.getFilteredMentionKnowledgeBases(query)
      const isSameMentionContext = this.showMention &&
        this.mentionStartIndex === atIndex &&
        this.mentionQuery === query
      this.mentionStartIndex = atIndex
      this.mentionQuery = query
      this.mentionActiveIndex = isSameMentionContext
        ? Math.min(this.mentionActiveIndex, Math.max(filteredMentionKnowledgeBases.length - 1, 0))
        : 0
      this.showMention = true
      this.syncMentionSelection()
    },
    closeMention() {
      this.showMention = false
      this.mentionQuery = ''
      this.mentionStartIndex = -1
      this.mentionActiveIndex = 0
    },
    selectMention(item) {
      const textarea = this.getTextareaEl()
      const value = this.messageText || ''
      const caret = textarea ? textarea.selectionStart || 0 : value.length
      if (this.mentionStartIndex < 0) {
        return
      }
      const before = value.slice(0, this.mentionStartIndex)
      const after = value.slice(caret)
      const nextValue = `${before}@${item.name} ${after}`
      this.$emit('update-message', nextValue)
      this.$nextTick(() => {
        const input = this.getTextareaEl()
        if (input) {
          const nextCaret = (before + '@' + item.name + ' ').length
          input.focus()
          input.setSelectionRange(nextCaret, nextCaret)
        }
        this.syncMentionSelection(nextValue)
        this.closeMention()
      })
    },
    syncMentionSelection(text) {
      const source = typeof text === 'string' ? text : (this.messageText || '')
      const ids = []
      const seen = new Set()
      ;(this.knowledgeBases || []).forEach(item => {
        const name = String(item.name || '')
        if (!name) {
          return
        }
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const pattern = new RegExp(`(^|\\s)@${escapedName}(?=\\s|$)`)
        if (pattern.test(source) && !seen.has(item.id)) {
          seen.add(item.id)
          ids.push(item.id)
        }
      })
      this.$emit('change-knowledge-bases', ids)
    }
  }
}
</script>

<style lang="less" scoped>
.conversation-main {
  background: #ffffff;
  border: 1px solid #e7eaed;
  border-radius: 6px;
}

.conversation-main {
  height: 720px;
  min-height: 720px;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.message-board {
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  border: 1px solid #e7eaed;
  border-radius: 6px;
  background: #f8f9fb;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-row.user {
  align-items: flex-end;
}

.message-row.assistant {
  align-items: flex-start;
}

.message-role {
  color: var(--muted-foreground);
  font-size: 12px;
  line-height: 18px;
}

.message-item {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 6px;
  line-height: 22px;
}

.message-item.user {
  background: var(--primary-alpha-08);
  color: var(--foreground);
}

.message-item.assistant {
  background: #f5f5f5;
  color: var(--foreground-secondary);
}

.message-content {
  font-size: 14px;
  word-break: break-word;
}

.message-content.user-text {
  white-space: pre-wrap;
}

.message-content.markdown-body {
  line-height: 22px;
}

.message-time {
  margin-top: 8px;
  opacity: 0.78;
  font-size: 12px;
}

::v-deep .markdown-body > :first-child {
  margin-top: 0;
}

::v-deep .markdown-body > :last-child {
  margin-bottom: 0;
}

::v-deep .markdown-body p {
  margin: 0 0 12px;
}

::v-deep .markdown-body h1,
::v-deep .markdown-body h2,
::v-deep .markdown-body h3,
::v-deep .markdown-body h4,
::v-deep .markdown-body h5,
::v-deep .markdown-body h6 {
  margin: 16px 0 12px;
  color: #24323b;
  font-weight: 600;
  line-height: 1.6;
}

::v-deep .markdown-body h1 {
  font-size: 22px;
}

::v-deep .markdown-body h2 {
  font-size: 20px;
}

::v-deep .markdown-body h3 {
  font-size: 18px;
}

::v-deep .markdown-body h4,
::v-deep .markdown-body h5,
::v-deep .markdown-body h6 {
  font-size: 16px;
}

::v-deep .markdown-body ul,
::v-deep .markdown-body ol {
  margin: 12px 0;
  padding-left: 20px;
}

::v-deep .markdown-body li {
  margin: 6px 0;
}

::v-deep .markdown-body hr {
  margin: 16px 0;
  border: 0;
  border-top: 1px solid #d8e1e6;
}

::v-deep .markdown-body blockquote {
  margin: 12px 0;
  padding: 8px 12px;
  color: #5b6b75;
  background: #f3f7fa;
  border-left: 4px solid #9ab2bd;
}

::v-deep .markdown-body pre {
  margin: 12px 0;
  padding: 12px;
  overflow-x: auto;
  background: #eef3f6;
  border-radius: 6px;
}

::v-deep .markdown-body code {
  padding: 2px 6px;
  color: #2d4752;
  background: #eef3f6;
  border-radius: 4px;
  font-size: 13px;
}

::v-deep .markdown-body pre code {
  display: block;
  padding: 0;
  background: transparent;
}

::v-deep .markdown-body a {
  color: #2d6f7a;
  text-decoration: underline;
}

::v-deep .markdown-body img {
  max-width: 100%;
  border-radius: 6px;
}

::v-deep .markdown-body table {
  display: block;
  width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
}

::v-deep .markdown-body th,
::v-deep .markdown-body td {
  padding: 8px 12px;
  border: 1px solid #d8e1e6;
  text-align: left;
  white-space: nowrap;
}

.composer {
  margin-top: 20px;
  flex-shrink: 0;
}

.composer-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.composer-hint {
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 20px;
}

.composer-input-wrap {
  position: relative;
}

.mention-chip-list {
  margin-bottom: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.mention-chip {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  background: #eef6fb;
  color: var(--primary);
  font-size: 12px;
  line-height: 18px;
}

.mention-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 8;
  border: 1px solid #dfe6ec;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 35, 41, 0.08);
}

.mention-list {
  max-height: 240px;
  overflow-y: auto;
}

.mention-item {
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
}

.mention-item:last-child {
  border-bottom: none;
}

.mention-item.active,
.mention-item:hover {
  background: #f6fbff;
}

.mention-item-name {
  color: var(--foreground-secondary);
  font-size: 14px;
  line-height: 20px;
}

.mention-item-meta,
.mention-empty {
  margin-top: 4px;
  color: var(--disabled-text);
  font-size: 12px;
  line-height: 18px;
}

.mention-empty {
  padding: 14px;
  margin-top: 0;
}

.composer-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

::v-deep .toolbar-field .el-select {
  width: 100%;
}

::v-deep .el-textarea__inner,
::v-deep .el-input__inner {
  border-radius: 6px;
}

@media (max-width: 768px) {
  .conversation-main {
    width: 100%;
    min-width: 0;
    height: 620px;
    min-height: 520px;
    max-height: calc(100vh - 104px);
    max-height: calc(100dvh - 104px);
    padding: 12px;
  }

  .message-board {
    padding: 12px;
    scrollbar-gutter: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .message-item {
    max-width: 92%;
    padding: 10px 12px;
  }

  .composer {
    min-width: 0;
    margin-top: 12px;
  }

  .composer-toolbar {
    margin-bottom: 8px;
  }

  .composer-hint {
    font-size: 12px;
    line-height: 18px;
  }

  .mention-list {
    max-height: 180px;
  }

  .composer-actions {
    margin-top: 8px;
  }

  ::v-deep .composer-send-btn {
    width: 100%;
  }
}
</style>
